import { useState, useEffect } from 'react';

interface NotificationState {
  isSupported: boolean;
  permission: NotificationPermission;
  isRequesting: boolean;
}

interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  actions?: NotificationAction[];
}

export const useNotifications = () => {
  const [state, setState] = useState<NotificationState>({
    isSupported: 'Notification' in window,
    permission: 'Notification' in window ? Notification.permission : 'denied',
    isRequesting: false
  });

  useEffect(() => {
    if (state.isSupported) {
      setState(prev => ({
        ...prev,
        permission: Notification.permission
      }));
    }
  }, [state.isSupported]);

  const requestPermission = async (): Promise<boolean> => {
    if (!state.isSupported) {
      return false;
    }

    if (state.permission === 'granted') {
      return true;
    }

    setState(prev => ({ ...prev, isRequesting: true }));

    try {
      const permission = await Notification.requestPermission();
      setState(prev => ({
        ...prev,
        permission,
        isRequesting: false
      }));
      
      return permission === 'granted';
    } catch (error) {
      console.error('Error al solicitar permisos de notificación:', error);
      setState(prev => ({ ...prev, isRequesting: false }));
      return false;
    }
  };

  const showNotification = async (options: NotificationOptions): Promise<boolean> => {
    if (!state.isSupported || state.permission !== 'granted') {
      return false;
    }

    try {
      // Si es una PWA instalada, usar el service worker para notificaciones
      if ('serviceWorker' in navigator && window.matchMedia('(display-mode: standalone)').matches) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.showNotification(options.title, {
            body: options.body,
            icon: options.icon || '/logo192.png',
            badge: options.badge || '/logo192.png',
            tag: options.tag,
            requireInteraction: options.requireInteraction || false,
            actions: options.actions || [],
            vibrate: [200, 100, 200],
            data: {
              timestamp: Date.now(),
              url: window.location.origin
            }
          });
          return true;
        }
      }

      // Fallback a notificación normal del navegador
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/logo192.png',
        tag: options.tag,
        requireInteraction: options.requireInteraction || false
      });

      // Auto-cerrar después de 5 segundos si no requiere interacción
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      return true;
    } catch (error) {
      console.error('Error al mostrar notificación:', error);
      return false;
    }
  };

  const showReminderNotification = (message: string) => {
    return showNotification({
      title: 'MalDolor - Recordatorio',
      body: message,
      icon: '/logo192.png',
      tag: 'reminder',
      requireInteraction: false
    });
  };

  const showUpdateNotification = () => {
    return showNotification({
      title: 'MalDolor - Actualización disponible',
      body: '¡Hay una nueva versión con mejoras esperándote!',
      icon: '/logo192.png',
      tag: 'update',
      requireInteraction: true,
      actions: [
        {
          action: 'update',
          title: 'Actualizar ahora'
        },
        {
          action: 'dismiss',
          title: 'Más tarde'
        }
      ]
    });
  };

  const showHeadacheReminder = () => {
    return showNotification({
      title: 'MalDolor - Registro diario',
      body: '¿Has tenido algún dolor de cabeza hoy? No olvides registrarlo.',
      icon: '/logo192.png',
      tag: 'headache-reminder',
      requireInteraction: false
    });
  };

  const showTensionReminder = () => {
    return showNotification({
      title: 'MalDolor - Control tensión',
      body: 'Es hora de medir tu tensión arterial.',
      icon: '/logo192.png',
      tag: 'tension-reminder',
      requireInteraction: false
    });
  };

  return {
    ...state,
    requestPermission,
    showNotification,
    showReminderNotification,
    showUpdateNotification,
    showHeadacheReminder,
    showTensionReminder
  };
};